// <------------------------ Seats ------------------------>

import { cineamoApiClient } from '../../apiClient';
import { apiErrorStatus } from '../../helpers/api-response-helper/api-response-helper';
import { buildUrlQuery, UrlQueryParameter } from '../../helpers/url-query-helper';
import { CinemaRoomDto } from '../../models/cinema-room/CinemaRoomDto.types';
import { SeatDto } from '../../models/seat/SeatDto.types';
import { SeatInputDto } from '../../models/seat/SeatInputDto';
import { SeatLookBulkItemInputDto } from '../../models/seat/SeatLookBulkItemInputDto';
import { SeatPositionDto } from '../../models/seat-position/SeatPositionDto.types';
import { SeatPositionInputDto } from '../../models/seat-position/SeatPositionInputDto';
import { ShapeDto } from '../../models/shape/ShapeDto.types';
import { ShapeInputDto } from '../../models/shape/ShapeInputDto';
import { SeatTransformBulkItemInputDto } from '../../models/transform/TransformBulkInputDto';
import { TransformDto } from '../../models/transform/TransformDto.types';
import { TransformInputDto } from '../../models/transform/TransformInputDto';
import { SeatListResponse } from '../../types/ApiListResponse.types';
import { ApiResponse } from '../../types/ApiResponse.types';

import { SeatApiEmbed } from './seat-api.embed';

type GetSeatsParams = UrlQueryParameter & {
    cinemaRoomId?: CinemaRoomDto['id'];
    embed?: SeatApiEmbed[];
};

export async function getSeats(urlParams?: GetSeatsParams): Promise<ApiResponse<SeatListResponse>> {
    return cineamoApiClient
        .get<ApiResponse<SeatListResponse>>(`/seats${buildUrlQuery(urlParams)}`)
        .then((response) => response.data);
}

// <------------------------ Seat ------------------------>

export async function getSeatById(id: SeatDto['id']): Promise<ApiResponse<SeatDto>> {
    return cineamoApiClient.get<ApiResponse<SeatDto>>(`/seats/${id}`).then((response) => response.data);
}

// <------------------------ Create Seat ------------------------>

export async function createSeat(seat: SeatInputDto): Promise<ApiResponse<SeatDto>> {
    return cineamoApiClient.post<ApiResponse<SeatDto>>('/seats', seat).then((response) => response.data);
}

// <------------------------ Update Seat ------------------------>

export async function updateSeatById(seatId: number, seat: SeatInputDto): Promise<ApiResponse<SeatDto>> {
    return cineamoApiClient.patch<ApiResponse<SeatDto>>(`/seats/${seatId}`, seat).then((response) => response.data);
}

// <------------------------ Delete Seat ------------------------>

export async function deleteSeatById(seatId: number): Promise<boolean> {
    return cineamoApiClient.delete(`/seats/${seatId}`).then((response) => response.status === 204);
}

/** Like `deleteSeatById` but reports the HTTP status (`null` for a network failure), so a caller can tell 404 from an outage. */
export async function deleteSeatByIdWithStatus(seatId: number): Promise<number | null> {
    return cineamoApiClient
        .delete(`/seats/${seatId}`)
        .then((response) => response.status)
        .catch((error: unknown) => apiErrorStatus(error) ?? null);
}

// <------------------------ Set Seat Shape ------------------------>

export async function setSeatShape(seatId: number, seatShape: ShapeInputDto): Promise<ApiResponse<ShapeDto>> {
    return cineamoApiClient
        .put<ApiResponse<ShapeDto>>(`/seats/${seatId}/shape`, seatShape)
        .then((response) => response.data);
}

// <------------------------ Set Seat Transform ------------------------>

export async function setSeatTransform(
    seatId: number,
    seatTransform: TransformInputDto
): Promise<ApiResponse<TransformDto>> {
    return cineamoApiClient
        .put<ApiResponse<TransformDto>>(`/seats/${seatId}/transform`, seatTransform)
        .then((response) => response.data);
}

// <------------------------ Set Seat Position ------------------------>

export async function setSeatPosition(
    seatId: number,
    seatPosition: SeatPositionInputDto
): Promise<ApiResponse<SeatPositionDto>> {
    return cineamoApiClient
        .put<ApiResponse<SeatPositionDto>>(`/seats/${seatId}/position`, seatPosition)
        .then((response) => response.data);
}

// <------------------------ Set Many Seat Looks ------------------------>

/**
 * Writes the own look of up to 500 seats of one room in a single transaction (all or nothing). Resolves with the
 * HTTP status (`null` for a network failure): 204 means every seat was written, 400 names the first item that is
 * not a seat of the room or whose look is not valid.
 */
export async function setSeatLooks(
    cinemaRoomId: CinemaRoomDto['id'],
    items: SeatLookBulkItemInputDto[]
): Promise<number | null> {
    return cineamoApiClient
        .patch(`/cinema-rooms/${cinemaRoomId}/seat-looks`, items)
        .then((response) => response.status)
        .catch((error: unknown) => apiErrorStatus(error) ?? null);
}

// <------------------------ Set Many Seat Transforms ------------------------>

/**
 * Moves up to 500 seats of one room in a single transaction (all or nothing). Resolves with the HTTP status
 * (`null` for a network failure): 204 means every seat was written, 400 names the first item that is not a
 * seat of the room.
 */
export async function setSeatTransforms(
    cinemaRoomId: CinemaRoomDto['id'],
    items: SeatTransformBulkItemInputDto[]
): Promise<number | null> {
    return cineamoApiClient
        .patch(`/cinema-rooms/${cinemaRoomId}/seat-transforms`, items)
        .then((response) => response.status)
        .catch((error: unknown) => apiErrorStatus(error) ?? null);
}
